import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  RadialBarChart,
  RadialBar,
} from "recharts";
import { DPAT_PASS_MARK_PERCENT } from "utils/utils";
import {
  DPAT_BRAND,
  DPAT_CHART,
  TIER_CHART_COLORS,
  tooltipStyle,
} from "../dpatChartTheme";

function ChartCard({ title, subtitle, children, className = "" }) {
  return (
    <div
      className={`rounded-xl border border-border bg-card p-4 shadow-sm md:p-6 ${className}`}
    >
      <h3 className="text-base font-semibold text-foreground md:text-lg">{title}</h3>
      {subtitle ? <p className="mt-1 mb-4 text-sm text-muted-foreground">{subtitle}</p> : null}
      {children}
    </div>
  );
}

const StatMini = ({ label, value, accent }) => (
  <div className="rounded-lg border border-border/80 bg-[#16325a]/5 px-4 py-3">
    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="mt-1 text-xl font-bold tabular-nums" style={{ color: accent || DPAT_BRAND }}>
      {value}
    </p>
  </div>
);

const DpatAnalyticsSection = ({ summary, reportingYear, loading, dataReady }) => {
  if (loading) {
    return (
      <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-80 animate-pulse rounded-xl border border-border bg-card" />
        ))}
      </div>
    );
  }

  if (!dataReady || summary.assessedCount === 0) {
    return (
      <div className="mt-8 rounded-xl border border-border bg-card px-4 py-10 text-center text-sm text-muted-foreground">
        Analytics will appear once district DPAT scores are loaded for {reportingYear}.
      </div>
    );
  }

  const tierColors = summary.tierPieChart.map(
    (entry) => TIER_CHART_COLORS[entry.name] || DPAT_CHART.slate,
  );

  return (
    <section className="mt-8 space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground md:text-2xl">Performance analytics</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          National comparisons for {reportingYear} — pass mark {DPAT_PASS_MARK_PERCENT}%
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatMini
          label="National average"
          value={summary.nationalAvg != null ? `${summary.nationalAvg.toFixed(1)}%` : "—"}
          accent={DPAT_CHART.teal}
        />
        <StatMini
          label="Median score"
          value={summary.medianPercent != null ? `${summary.medianPercent.toFixed(1)}%` : "—"}
          accent={DPAT_CHART.navy}
        />
        <StatMini
          label="Passed districts"
          value={summary.passCount}
          accent={DPAT_CHART.emerald}
        />
        <StatMini
          label="Below pass"
          value={summary.failCount}
          accent={DPAT_CHART.coral}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartCard
          title="Performance tier mix"
          subtitle="Share of assessed districts by classification"
        >
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={summary.tierPieChart}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={52}
                  outerRadius={88}
                  paddingAngle={3}
                  label={({ name, percent }) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                >
                  {summary.tierPieChart.map((entry, i) => (
                    <Cell key={entry.name} fill={tierColors[i]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard
          title="Pass vs below pass"
          subtitle={`Districts at or above ${DPAT_PASS_MARK_PERCENT}% Final Outcome`}
        >
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart
                cx="50%"
                cy="50%"
                innerRadius="30%"
                outerRadius="88%"
                barSize={14}
                data={summary.passFailChart.map((d, i) => ({
                  name: d.name,
                  value: d.value,
                  fill: i === 0 ? DPAT_CHART.emerald : DPAT_CHART.coral,
                }))}
                startAngle={90}
                endAngle={-270}
              >
                <RadialBar background dataKey="value" cornerRadius={8} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </RadialBarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartCard
          title="SDI, PI and CI national average"
          subtitle="Service Delivery, Performance, and Compliance indicator pillars"
        >
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={summary.sdiPiChart} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(22,50,90,0.08)" />
                <XAxis dataKey="pillar" tick={{ fontSize: 10 }} interval={0} angle={-8} height={56} textAnchor="end" />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                <Tooltip
                  formatter={(v) => [`${v}%`, "Average"]}
                  contentStyle={tooltipStyle}
                />
                <Bar dataKey="percent" name="Average %" radius={[6, 6, 0, 0]}>
                  {summary.sdiPiChart.map((_, i) => (
                    <Cell
                      key={i}
                      fill={i === 0 ? DPAT_CHART.teal : i === 1 ? DPAT_CHART.amber : DPAT_CHART.violet}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard
          title="Overall score distribution"
          subtitle="How districts cluster by Final Outcome %"
        >
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={summary.scoreBuckets} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <defs>
                  <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={DPAT_CHART.navy} stopOpacity={0.45} />
                    <stop offset="100%" stopColor={DPAT_CHART.navy} stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(22,50,90,0.08)" />
                <XAxis dataKey="range" tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Area
                  type="monotone"
                  dataKey="count"
                  name="Districts"
                  stroke={DPAT_BRAND}
                  fill="url(#scoreGrad)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <ChartCard
          className="lg:col-span-7"
          title="Regional average Final Outcome"
          subtitle="Top regions by mean district score"
        >
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={summary.regionAvgChart}
                layout="vertical"
                margin={{ top: 4, right: 16, left: 4, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(22,50,90,0.08)" />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                <YAxis type="category" dataKey="region" width={108} tick={{ fontSize: 10 }} />
                <Tooltip
                  formatter={(v) => [`${v}%`, "Regional avg"]}
                  contentStyle={tooltipStyle}
                />
                <Bar dataKey="average" name="Avg %" fill={DPAT_BRAND} radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard
          className="lg:col-span-5"
          title="Compliance fulfilment"
          subtitle="Districts grouped by % of compliance indicators met"
        >
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={summary.complianceBands} margin={{ top: 8, right: 8, left: 0, bottom: 48 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(22,50,90,0.08)" />
                <XAxis dataKey="band" tick={{ fontSize: 9 }} angle={-18} height={64} textAnchor="end" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" name="Districts" fill={DPAT_CHART.violet} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </div>

      {summary.regionCoverageChart.length > 0 && (
        <ChartCard
          title="Assessment coverage by region"
          subtitle="Assessed vs districts still without DPAT score data (top 10 regions)"
        >
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={summary.regionCoverageChart}
                margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(22,50,90,0.08)" />
                <XAxis dataKey="region" tick={{ fontSize: 10 }} interval={0} angle={-14} height={52} textAnchor="end" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="assessed" name="With score" stackId="a" fill={DPAT_CHART.teal} radius={[0, 0, 0, 0]} />
                <Bar dataKey="missing" name="No score" stackId="a" fill={DPAT_CHART.sand} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      )}

      <ChartCard
        title="Classification headcount"
        subtitle="Number of districts in each performance tier"
      >
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={summary.performanceBars} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(22,50,90,0.08)" />
              <XAxis dataKey="tier" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" name="Districts" radius={[6, 6, 0, 0]}>
                {summary.performanceBars.map((entry) => (
                  <Cell
                    key={entry.tier}
                    fill={TIER_CHART_COLORS[entry.tier] || DPAT_CHART.slate}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>
    </section>
  );
};

export default DpatAnalyticsSection;
